import { Table } from '@radix-ui/themes'
import { flexRender, Row } from '@tanstack/react-table'
import React from 'react'
import { css, styled } from 'styled-components'

import {
  GECKO_POWER_TABLE_FLAT_DENSITY,
  GECKO_POWER_TABLE_ROW_BG,
  GECKO_POWER_TABLE_ROW_HOVER_BG,
} from './TableStyles'

const StyledTableCell = styled(Table.Cell)`
  color: var(--foreground);
  padding: var(
    --table-cell-padding,
    ${GECKO_POWER_TABLE_FLAT_DENSITY.cellPadding}
  );
  text-align: left;
  vertical-align: middle;
  box-sizing: border-box;
  height: var(--table-row-height, ${GECKO_POWER_TABLE_FLAT_DENSITY.rowHeight});
  min-height: var(
    --table-row-height,
    ${GECKO_POWER_TABLE_FLAT_DENSITY.rowHeight}
  );
  border-style: hidden !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 22rem;
  font-size: var(
    --table-cell-font-size,
    ${GECKO_POWER_TABLE_FLAT_DENSITY.fontSize}
  );
  font-weight: 400;
  line-height: var(
    --table-cell-line-height,
    ${GECKO_POWER_TABLE_FLAT_DENSITY.lineHeight}
  );
  --table-row-box-shadow: none;
`

const StyledFirstCell = styled(Table.Cell)`
  display: flex;
  align-items: center;
  min-width: 0;
`

const StyledTableRow = styled(Table.Row)<{
  $hasOnClick: boolean
  $depth?: number
  $enableExpanding?: boolean
  $isHighlighted?: boolean
  $isHovered?: boolean
}>`
  position: relative;
  outline: none;
  scroll-margin-top: 3rem;
  height: var(--table-row-height, ${GECKO_POWER_TABLE_FLAT_DENSITY.rowHeight});
  background-color: ${GECKO_POWER_TABLE_ROW_BG};

  ${StyledTableCell} {
    border-bottom: 1px solid var(--border) !important;
  }

  td:first-child,
  td:last-child {
    border-radius: 0;
  }

  &:focus {
    ${StyledTableCell} {
      background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
    }
  }

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      ${StyledTableCell} {
        background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
      }
    `}

  ${({ $isHovered }) =>
    $isHovered &&
    css`
      ${StyledTableCell} {
        background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
      }
    `}

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
        }
      }
    `}

  ${({ $depth = 0, $enableExpanding }) =>
    $depth > 0 &&
    css`
      ${StyledTableCell}:first-child {
        padding-left: ${$depth * 1.5}rem;
        position: relative;

        ${$enableExpanding &&
        css`
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: ${$depth * 1.5}rem;
            background-color: ${({ theme }) => theme.forecast.pageBackground1};
            z-index: 0;
          }
        `}
      }
    `}
`

const ExpandButton = styled.button.attrs({
  type: 'button',
})<{ $isPlaceholder?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.25rem;
  margin-left: 0.5rem;
  width: 1rem;
  min-width: 1rem;
  height: 1rem;
  min-height: 1rem;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--muted-foreground);
  font-size: 0.75rem;
  line-height: 1;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    color: var(--foreground);
    background-color: ${GECKO_POWER_TABLE_ROW_HOVER_BG};
  }

  ${({ $isPlaceholder }) =>
    $isPlaceholder &&
    css`
      opacity: 0;
    `}
`

interface TableBodyRowProps<TData extends object> {
  row: Row<TData>
  enableExpanding?: boolean
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowAuxClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowContextMenu?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowKeyDown?: (params: {
    event: React.KeyboardEvent<HTMLTableRowElement>
    row: Row<TData>
    rowElement: HTMLTableRowElement
  }) => void
  onRowFocus?: (row: Row<TData>) => void
  onRowHoverStart?: (row: Row<TData>) => void
  onRowHoverEnd?: () => void
  $isHighlighted?: boolean
  $isHovered?: boolean
  htmlAttributes?: React.HTMLAttributes<HTMLTableRowElement>
}

export const TableBodyRow = <TData extends object>({
  row,
  enableExpanding,
  onRowClick,
  onRowAuxClick,
  onRowContextMenu,
  onRowKeyDown,
  onRowFocus,
  onRowHoverStart,
  onRowHoverEnd,
  $isHighlighted,
  $isHovered,
  htmlAttributes,
}: TableBodyRowProps<TData>) => {
  const canExpand = enableExpanding && row.getCanExpand()
  const isExpanded = enableExpanding && row.getIsExpanded()

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (enableExpanding && canExpand) {
      row.toggleExpanded()
    }

    if (onRowClick) {
      onRowClick(row, event)
    }
  }

  const handleAuxClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (onRowAuxClick) {
      onRowAuxClick(row, event)
    }
  }

  const handleContextMenu = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (onRowContextMenu) {
      onRowContextMenu(row, event)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (onRowKeyDown) {
      onRowKeyDown({ event, row, rowElement: event.currentTarget })
    }
  }

  const handleFocus = () => {
    if (onRowFocus) {
      onRowFocus(row)
    }
  }

  const handleMouseEnter = () => {
    if (onRowHoverStart) {
      onRowHoverStart(row)
    }
  }

  const handleMouseLeave = () => {
    if (onRowHoverEnd) {
      onRowHoverEnd()
    }
  }

  return (
    <StyledTableRow
      key={row.id}
      id={row.id}
      tabIndex={onRowKeyDown ? 0 : undefined}
      data-state={row.getIsSelected() && 'selected'}
      data-depth={row.depth}
      onClick={handleRowClick}
      onAuxClick={handleAuxClick}
      onContextMenu={handleContextMenu}
      onKeyDown={onRowKeyDown ? handleKeyDown : undefined}
      onFocus={onRowFocus ? handleFocus : undefined}
      onMouseEnter={onRowHoverStart ? handleMouseEnter : undefined}
      onMouseLeave={onRowHoverEnd ? handleMouseLeave : undefined}
      $hasOnClick={Boolean(onRowClick || (enableExpanding && canExpand))}
      $depth={row.depth}
      $enableExpanding={enableExpanding}
      $isHighlighted={$isHighlighted}
      $isHovered={$isHovered}
      {...htmlAttributes}>
      {row.getVisibleCells().map((cell, index) => (
        <StyledTableCell key={cell.id}>
          {index === 0 && enableExpanding ? (
            <StyledFirstCell>
              <ExpandButton
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
                $isPlaceholder={!canExpand}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                {isExpanded ? '▼' : '▶'}
              </ExpandButton>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </StyledFirstCell>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
}
